import { ApolloProvider } from "@apollo/client";
import { init } from "@module-federation/enhanced/runtime";
import { StyledEngineProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ThemeProvider } from "@teamworksdev/mui";
import "@teamworksdev/nav/dist/index.css";
import "@teamworksdev/shadcn/dist/index.css";
import "@teamworksdev/style/dist/index.css";
import React from "react";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";
import { ClientTimezoneContextProvider } from "shared-ui/contexts/ClientTimezone";
import App from "./App";
import client from "./client";
import { SnackbarContextProvider } from "./components/Snackbar/SnackbarContext";
import { CookieContextProvider } from "./contexts/Cookie";
import { MultiTabManagerProvider } from "./contexts/MultiTabManager";
import { TeamworksAuthContextProvider } from "./contexts/TeamworksAuth";
import "./i18n"; // Initialize i18next
import { AppThemeProvider } from "./pages/Theme/AppThemeProvider";

// Register Nutrition's React/ReactDOM in the Module Federation shared scope so
// remote MFEs (e.g. messaging) reuse this exact instance instead of bundling
// their own copy. Without this, hooks called inside a remote run against a
// different React instance than the one mounted to the DOM, producing
// "Invalid hook call" errors and `removeChild` failures.
init({
  name: "teamworks_nutrition",
  remotes: [],
  shared: {
    react: {
      version: React.version,
      lib: () => React,
      shareConfig: { singleton: true, requiredVersion: ">=18" },
    },
    "react-dom": {
      version: ReactDOM.version,
      lib: () => ReactDOM,
      shareConfig: { singleton: true, requiredVersion: ">=18" },
    },
  },
});

// Use `createRoot` so the host runs in React 18 concurrent mode, matching
// remote MFEs. Legacy `ReactDOM.render` triggers compat mode and the mismatch
// caused remount/race issues that fed an ErrorBoundary reload loop.
const root = createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <CookieContextProvider>
    <StyledEngineProvider injectFirst>
      <ThemeProvider>
        <AppThemeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ApolloProvider client={client}>
              <TeamworksAuthContextProvider>
                <ClientTimezoneContextProvider>
                  <SnackbarContextProvider>
                    <BrowserRouter>
                      <CompatRouter>
                        <MultiTabManagerProvider>
                          <App />
                        </MultiTabManagerProvider>
                      </CompatRouter>
                    </BrowserRouter>
                  </SnackbarContextProvider>
                </ClientTimezoneContextProvider>
              </TeamworksAuthContextProvider>
            </ApolloProvider>
          </LocalizationProvider>
        </AppThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  </CookieContextProvider>,
);
